/**
 * MarketTrendsChart normalization utilities
 *
 * Combining 4 metrics with genuinely different units (days, a month-count,
 * a ratio, a percentage) onto one shared axis without a dual/multi-axis
 * chart (the #1 charting mistake per the dataviz skill) means indexing
 * each metric to a common base: its own seller/buyer thresholds become
 * fixed points (+1 / -1) on a shared scale, so "how far into seller or
 * buyer territory is this metric" is directly comparable across series
 * even though the raw numbers never were.
 */
/**
 * Maps a raw metric value onto a shared index where +1 = exactly at the
 * seller threshold, -1 = exactly at the buyer threshold, 0 = the midpoint
 * between them (center of the "balanced" zone). Values beyond either
 * threshold extend past +-1 (deeper into that market) - callers typically
 * clamp for display, since a handful of extreme months (some of Maui's
 * real data is 5-6x past a threshold) would otherwise flatten the whole
 * chart's scale.
 */
export function computeMarketIndex(value, bands) {
    const mid = (bands.sellerThreshold + bands.buyerThreshold) / 2;
    const halfRange = Math.abs(bands.sellerThreshold - bands.buyerThreshold) / 2;
    if (halfRange === 0)
        return 0;
    const raw = (value - mid) / halfRange;
    return bands.polarity === 'lowIsSeller' ? -raw : raw;
}
export function clampIndex(index, limit) {
    return Math.max(-limit, Math.min(limit, index));
}
/**
 * Formats a metric's buyer/balanced/seller cutoffs as display strings for
 * the "?" help panel, using `format` so the same source-of-truth thresholds
 * that drive the chart also drive what a reader is told - they can never
 * drift apart the way a hand-copied number in a doc comment or memory file
 * eventually would.
 */
export function describeBandRanges(bands, format) {
    const lo = Math.min(bands.sellerThreshold, bands.buyerThreshold);
    const hi = Math.max(bands.sellerThreshold, bands.buyerThreshold);
    const balanced = `${format(lo)} - ${format(hi)}`;
    return bands.polarity === 'lowIsSeller'
        ? { seller: `< ${format(lo)}`, balanced, buyer: `> ${format(hi)}` }
        : { buyer: `< ${format(lo)}`, balanced, seller: `> ${format(hi)}` };
}
//# sourceMappingURL=utils.js.map